import React, { useState } from 'react';
import { 
  CreditCard, 
  Calendar, 
  Clock, 
  Phone, 
  Shield, 
  AlertTriangle, 
  ExternalLink,
  MapPin,
  Mail,
  Globe
} from 'lucide-react';

const TermsAndConditionsPage: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const paymentMethods = [
    {
      title: "Bank Transfer (Preferred)",
      details: [
        "Bank Name: CashPlus",
        "Sort Code: 087199",
        "Account Number: 02926739",
        "Account Name: Twin Brain Ltd",
        "Payment Reference: Your initials, surname, and date you received the service e.g., J. Kane23/06/16"
      ]
    },
    {
      title: "Direct Debit",
      details: ["Clients can set up a direct debit payment using their bank"]
    },
    {
      title: "Card Payments",
      details: [
        "Paying with debit, credit card, bank or making payments involving international currency conversion",
        "Clients can pay via GoCardless or their bank",
        "They may charge you a small fee, which is in addition to the full fee you are paying Health City",
        "Please check with your card issuer or bank before making a payment"
      ]
    }
  ];

  const insuranceProviders = [
    "Standard Life", "Cigna", "AVIVA", "AXA PPP", "BUPA International", "Vitality"
  ];

  const emergencyContacts = [
    { name: "Emergency Services", number: "999", description: "If you have or feel you are suicidal or about to harm yourself, or harm someone else" },
    { name: "Accident and Emergency (A&E)", number: "", description: "Go to A&E department nearest to your location. If you call 999, the call handler shall tell you the nearest A&E that will assist you" },
    { name: "Samaritans", number: "08457 909090", description: "24-hour crisis line for people contemplating harming themselves", website: "www.samaritans.org" },
    { name: "Saneline", number: "0300 304 7000, 07984 967 708", description: "Practical information, crisis care and emotional support" },
    { name: "Your GP", number: "", description: "Contact your GP on their phone number during your surgery working hours" },
    { name: "Single Point of Access Crisis Helpline", number: "", description: "Google the Single Point of Access Crisis Helpline in your Borough of residence and ask for help 24 hours/seven days" },
    { name: "NHS Direct", number: "0845 4647", description: "24 hours a day" },
    { name: "NHS 111", number: "111", description: "24 hours a day, 7 days a week. For urgent medical problems" }
  ];

  const usefulWebsites = [
    { name: "MoodGym", url: "http://moodgym.anu.edu.au", description: "Free online self-help course" },
    { name: "CCI Health", url: "www.cci.health.wa.gov.au", description: "Free self-help workbooks for emotional or mental health problems" },
    { name: "Living Life to the Full", url: "www.livinglifetothefull.com", description: "Free online life skills course working" }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Terms and Conditions
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            By booking our services, you accept the following terms and conditions. 
            Please read carefully before proceeding with any booking.
          </p>
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 font-semibold">
              BY BOOKING I ACCEPT THE FOLLOWING:
            </p>
          </div>
        </div>

        {/* Section 1: Payment Methods */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div 
            className="p-6 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('payments')}
          >
            <div className="flex items-center gap-3">
              <CreditCard className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">1. Payment Methods</h2>
            </div>
            <div className="text-gray-400">
              {expandedSection === 'payments' ? '−' : '+'}
            </div>
          </div>
          
          {expandedSection === 'payments' && (
            <div className="px-6 pb-6 space-y-6">
              {/* Self-Funding Clients */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">1.1 Self-Funding Clients</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {paymentMethods.map((method, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">{method.title}</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        {method.details.map((detail, idx) => (
                          <li key={idx}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Insurance Funding */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">1.2 Funding by Insurance or Organisation</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-gray-700 mb-3">
                    Assessment, Therapy, Counselling and Wellbeing Services can be paid via corporate organizations or all health insurance firms including:
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {insuranceProviders.map((provider, index) => (
                      <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                        {provider}
                      </span>
                    ))}
                  </div>
                  <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                    <p><strong>Note:</strong> BUPA UK is only accepted by some of our therapists. AXA PPP clients need a psychiatric referral first.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Payments and Cancellation Policy */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div 
            className="p-6 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('cancellation')}
          >
            <div className="flex items-center gap-3">
              <Calendar className="h-6 w-6 text-red-600" />
              <h2 className="text-xl font-bold text-gray-900">2. Payments and Cancellation Policy</h2>
            </div>
            <div className="text-gray-400">
              {expandedSection === 'cancellation' ? '−' : '+'}
            </div>
          </div>
          
          {expandedSection === 'cancellation' && (
            <div className="px-6 pb-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <h3 className="text-lg font-semibold text-red-800 mb-3">2.1 Payment Policy</h3>
                  <p className="text-red-700">
                    Payments for services are in advance. This can be via any of the payment options offered by Twin Brain Ltd. A receipt will be emailed to you.
                  </p>
                </div>
                
                <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                  <h3 className="text-lg font-semibold text-orange-800 mb-3">2.2 Cancellation Policy</h3>
                  <p className="text-orange-700 mb-3">
                    <strong>(a) Booked Assessment, Therapy, Counselling or Wellbeing Service:</strong> Evidence is that regular attendance helps with recovery, so to motivate attendance, the full fee will be paid for missed or cancelled sessions even with advance notice. Also, another client would have benefited from the missed or cancelled session. Risk and wellbeing review and self-help resources shall be provided in the event of a paid cancelled session.
                  </p>
                  <p className="text-orange-700">
                    <strong>(b) Other Services:</strong> Services booked cannot be cancelled, but must be paid in full whether used or not. Money is not refundable except Health City (HC) or Twin Brain Ltd failed to deliver or provide an alternative when it (HC) or Twin Brain Ltd could not offer the original service promised.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-blue-800 mb-3">2.3 Prices/Fees</h3>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-blue-700 mb-2">(a) Prices can be changed anytime without notice</h4>
                      <p className="text-blue-700">This shall not affect Bookings previously submitted.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-blue-700 mb-2">(b) Fees paid Online during Booking shall cover:</h4>
                      <ul className="list-disc list-inside text-blue-700 space-y-1 ml-2">
                        <li>Face to Face appointments where the client comes to the Health City's or Twin Brain Ltd.'s personnel at Canary Wharf UK, London Bridge UK or another venue (the UK or overseas) where the Health City's personnel is based</li>
                        <li>Services received Online or via Email, Phone, Text Messaging, Postal Letter or Fax</li>
                        <li>Services received in English language or in a language spoken by the Health City's personnel</li>
                        <li>The client does not mind their full identity known to the Health City's or Twin Brain Ltd's personnel</li>
                        <li>The client wants a Health City's or Twin Brain Ltd's personnel who is of a specific gender or who is aware of a specific faith or culture (Subject to availability)</li>
                        <li>A session of any service is 50 minutes. Consultation requires a double session (so the client pays for 2 sessions)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-blue-700 mb-2">(c) Fees Paid Online during Booking do not cover extra resource required by a client:</h4>
                      <p className="text-blue-700 mb-2">The extra resources will incur a negotiated additional fee to reflect the Health City's or Twin Brain Ltd's personnel's extra resource used by the client. You should complete the Online Booking with payment as normal and then contact Health City or Twin Brain Ltd to negotiate the cost of the extra resource. If you and Health City or Twin Brain Ltd cannot reach an agreeable cost for the extra resource, then Health City or Twin Brain Ltd will cancel your booking with a full refund of the fee you paid during the booking.</p>
                      <p className="font-semibold text-blue-700 mb-1">The following are examples of extra resources:</p>
                      <ul className="list-disc list-inside text-blue-700 space-y-1 ml-2">
                        <li>Travel expenses and time away from the clinic: Face to Face appointments or services where the Health City's or Twin Brain Ltd's personnel is required to use a public or a private transport to visit the client's home, school, office or other agreed venue within London, Outside London, or Overseas</li>
                        <li>Interpreter's fee: Services received in a language that requires an interpreter (including sign language)</li>
                        <li>Identity protection fee: The client wants their identity protected from the Health City's or Twin Brain Ltd's personnel but not from Health City or Twin Brain Ltd</li>
                        <li>Extra time fee: Family Therapy and Exposure Behavioural Programmes are likely to last more than one hour at times</li>
                        <li>Extra fee: Any other preference the client wants Health City or Twin Brain Ltd to consider that may require extra resource</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">2.4 Late Payments</h3>
                <p className="text-yellow-700">
                  Please note that late payment fee of five percent (5%) and the full VAT of the amount owed (and the late payment fee) will be applied to your invoice on a weekly basis if payment is not received by the due date. The ONLY time the late payment fee can be waived is if you contacted us before the due date to give us an agreeable (to us) reason for exception and extension of the due date. We reserved the right to use debt collectors or HMRC Court to recover unpaid fees.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">2.5 UK VAT</h3>
                <p className="text-gray-700">
                  VAT charged from 01/01/2015 for VAT chargeable services as HC becomes VAT registered.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Waiting Rooms */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div 
            className="p-6 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('waitingrooms')}
          >
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-purple-600" />
              <h2 className="text-xl font-bold text-gray-900">3. Waiting Rooms</h2>
            </div>
            <div className="text-gray-400">
              {expandedSection === 'waitingrooms' ? '−' : '+'}
            </div>
          </div>
          
          {expandedSection === 'waitingrooms' && (
            <div className="px-6 pb-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-purple-700">
                  Please note that the waiting rooms in some of our offices or clinics are closed evenings and weekends, so clients will be asked to meet their professional (e.g., therapist) at the exact appointment time.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section 4: Referral */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div 
            className="p-6 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('referral')}
          >
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">4. Referral</h2>
            </div>
            <div className="text-gray-400">
              {expandedSection === 'referral' ? '−' : '+'}
            </div>
          </div>
          
          {expandedSection === 'referral' && (
            <div className="px-6 pb-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-800 mb-2">4.1 Self-Referral</h3>
                <p className="text-green-700">
                  I agreed to refer myself to Health City or Twin Brain Ltd. I agreed that Health City or Twin Brain Ltd shall contact me for appointment, assessment and service provision.
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-800 mb-2">4.2 GP/Medical Doctor Responsibility</h3>
                <p className="text-green-700">
                  I agreed that even if I refused for information to be shared with my GP/Family or Private Medical Doctor (Health City or Twin Brain Ltd respects your decision and privacy), my GP/Family or Private Medical Doctor (and not Health City or Twin Brain Ltd) remains entity who holds the medical responsibility for my medications and healthcare.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section 5: Sharing Information */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div 
            className="p-6 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('information')}
          >
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">5. Sharing Information</h2>
            </div>
            <div className="text-gray-400">
              {expandedSection === 'information' ? '−' : '+'}
            </div>
          </div>
          
          {expandedSection === 'information' && (
            <div className="px-6 pb-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">5.1 Internal Information Sharing</h3>
                <p className="text-blue-700">
                  I agreed for my information to be confidentially shared within Health City or Twin Brain for the purpose of providing me a better care, as well as Health City's or Twin Brain Ltd's supervision and training.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">5.2 GDPR Compliance</h3>
                <p className="text-blue-700">
                  Health City or Twin Brain Ltd will not share your personal information or data without your consent. Health City upholds The General Data Protection Regulation (GDPR).
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section 8: Opening Hours */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div 
            className="p-6 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('hours')}
          >
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-indigo-600" />
              <h2 className="text-xl font-bold text-gray-900">8. Opening Hours</h2>
            </div>
            <div className="text-gray-400">
              {expandedSection === 'hours' ? '−' : '+'}
            </div>
          </div>
          
          {expandedSection === 'hours' && (
            <div className="px-6 pb-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="text-indigo-700">
                  <p><strong>Monday-Friday:</strong> 8am-11pm</p>
                  <p><strong>Saturday:</strong> 8am-9pm</p>
                  <p><strong>Sunday:</strong> Closed</p>
                  <p className="text-sm mt-2 italic">*This may change due to unforeseen circumstance</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 6: Emergency Support */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div 
            className="p-6 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('emergency')}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h2 className="text-xl font-bold text-gray-900">6. Risk of Harm and Emergency Support</h2>
            </div>
            <div className="text-gray-400">
              {expandedSection === 'emergency' ? '−' : '+'}
            </div>
          </div>
          
          {expandedSection === 'emergency' && (
            <div className="px-6 pb-6 space-y-6">
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 space-y-3">
                <div>
                  <h3 className="text-lg font-bold text-red-800 mb-2">6.1 NOT AN EMERGENCY SERVICE</h3>
                  <p className="text-red-700">
                    I agreed that Health City or Twin Brain Ltd is not an emergency service, so its email, telephone, personnel, and service cannot and must not to be used in an emergency.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-bold text-red-800 mb-2">6.2 Immediate Risk</h3>
                  <p className="text-red-700">
                    If I am in distress and worried about the immediate risk of harm to myself or others, I will go to or contact my nearest Accident & Emergency service (a visit will not be practical if there is a national emergency such as a virus outbreak).
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-red-800 mb-2">6.3 Support When Not at Immediate Risk</h3>
                  <p className="text-red-700">
                    If I am not at immediate risk but in distress and like to have support and advice, I will contact my GP/Family or Private Medical Doctor.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">6.3.1 Helplines for UK Residents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {emergencyContacts.map((contact, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Phone className="h-4 w-4 text-red-600" />
                        <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                      </div>
                      {contact.number && (
                        <p className="text-blue-600 font-mono mb-1">{contact.number}</p>
                      )}
                      <p className="text-sm text-gray-700">{contact.description}</p>
                      {contact.website && (
                        <p className="text-sm text-blue-600 mt-1">Website: {contact.website}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">NHS 111 British Sign Language if you are Deaf and want a phone service:</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• England – NHS 111 (BSL) interpreter service</li>
                  <li>• Scotland – NHS 24 111</li>
                  <li>• Wales – NHS 111 Wales</li>
                  <li>• Northern Ireland – NHS 111 Northern Ireland</li>
                  <li>• Or call 18001 111 on a textphone</li>
                </ul>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2">6.3.2 Helplines for Overseas Residents</h4>
                <p className="text-orange-700">
                  Contact your friends, family members, next of kin and your local Accident and Emergencies in your country.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section 7: Useful Resources */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div 
            className="p-6 cursor-pointer flex items-center justify-between"
            onClick={() => toggleSection('resources')}
          >
            <div className="flex items-center gap-3">
              <Globe className="h-6 w-6 text-purple-600" />
              <h2 className="text-xl font-bold text-gray-900">7. Useful Websites</h2>
            </div>
            <div className="text-gray-400">
              {expandedSection === 'resources' ? '−' : '+'}
            </div>
          </div>
          
          {expandedSection === 'resources' && (
            <div className="px-6 pb-6">
              <p className="text-sm text-gray-600 mb-4">
                <em>Health City or Twin Brain Ltd is not responsible and reliable for the contents of external websites.</em>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {usefulWebsites.map((site, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ExternalLink className="h-4 w-4 text-purple-600" />
                      <h4 className="font-semibold text-gray-900">{site.name}</h4>
                    </div>
                    <a href={site.url.startsWith('http') ? site.url : `https://${site.url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm mb-2 hover:underline block">
                      {site.url}
                    </a>
                    <p className="text-sm text-gray-700">{site.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">9. Contact</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Twin Brain Contact */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">9.1 Contact One: Twin Brain</h3>
                <div className="space-y-2 text-gray-700">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-1 text-gray-500" />
                    <p className="text-sm">
                      Twin Brain<br />
                      Davenport House<br />
                      16 Pepper Street<br />
                      Canary Wharf<br />
                      London E14 9RP<br />
                      United Kingdom
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <a href="mailto:admin@twinbrain.org" className="text-blue-600 text-sm hover:underline">admin@twinbrain.org</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <p className="text-sm">0333 800 3006, 0333 789 0012</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <a href="http://www.twinbrain.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">http://www.twinbrain.org</a>
                  </div>
                </div>
              </div>

              {/* Health City Contact */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">9.2 Contact Two: Health City</h3>
                <div className="space-y-2 text-gray-700">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-1 text-gray-500" />
                    <p className="text-sm">
                      Health City<br />
                      Davenport House<br />
                      16 Pepper Street<br />
                      Canary Wharf<br />
                      London E14 9RP<br />
                      United Kingdom
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <a href="mailto:info@healthcity.org.uk" className="text-blue-600 text-sm hover:underline">info@healthcity.org.uk</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <p className="text-sm">0333 800 3006, 0333 789 0012</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <a href="http://www.healthcity.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">http://www.healthcity.org.uk</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Facebook: </span>
                    <a href="https://www.facebook.com/healthcity.org.uk1" target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">www.facebook.com/healthcity.org.uk1</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Section */}
        <div className="mt-16 bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Ready to Get Started?</h2>
            <p className="text-lg text-gray-700 mb-6">
              Now that you've reviewed our terms and conditions, you can proceed with booking your appointment. 
              We're here to support your mental health and wellbeing journey.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/book-now" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                Book Appointment Now
              </a>
              <a 
                href="/contact" 
                className="bg-slate-600 hover:bg-slate-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Contact Us
              </a>
            </div>

            <div className="mt-6 p-4 bg-white border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Quick Contact Information:</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm text-gray-700">
                <div className="flex items-center justify-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>0333 800 3006 | 0333 789 0012</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>info@healthcity.org.uk</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-600">
          <p className="text-sm">
            These terms and conditions are effective and subject to change without notice.
          </p>
          <p className="text-sm mt-2">
            By using our services, you acknowledge that you have read, understood, and agree to these terms.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;